import React from "react";
import PropTypes from "prop-types";

// One pill, many variants. Presentation only — cards decide which badges apply.
// Icons and colors are taken verbatim from the V2 Source Directory Figma.

function BoltIcon(props) {
  return (
    <svg viewBox="0 0 11 11" fill="currentColor" {...props}>
      <path d="M5.95833 0.916667L1.375 6.41667H4.58333L4.125 10.0833L8.70833 4.58333H5.5L5.95833 0.916667Z" />
    </svg>
  );
}

function CommentIcon(props) {
  return (
    <svg viewBox="0 0 11 11" fill="currentColor" {...props}>
      <path d="M1.83333 1.83333H9.16667C9.67083 1.83333 10.0833 2.24583 10.0833 2.75V7.79167C10.0833 8.29583 9.67083 8.70833 9.16667 8.70833H6.875L5.04167 10.5417V8.70833H1.83333C1.32917 8.70833 0.916667 8.29583 0.916667 7.79167V2.75C0.916667 2.24583 1.32917 1.83333 1.83333 1.83333Z" />
    </svg>
  );
}

function StarIcon(props) {
  return (
    <svg viewBox="0 0 11 11" fill="currentColor" {...props}>
      <path d="M5.5 0.916667L6.82917 3.7125L9.85417 4.125L7.65417 6.23333L8.20417 9.25833L5.5 7.79167L2.79583 9.25833L3.34583 6.23333L1.14583 4.125L4.17083 3.7125L5.5 0.916667Z" />
    </svg>
  );
}

function CheckIcon(props) {
  return (
    <svg
      viewBox="0 0 11 11"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M9.16667 2.75L4.125 7.79167L1.83333 5.5" />
    </svg>
  );
}

const VARIANTS = {
  fast_to_respond: { label: "Fast to Respond", color: "#1e874c", background: "#e7f7ee", Icon: BoltIcon },
  frequent_source: { label: "Frequent Source", color: "#0039d3", background: "#e3f2fe", Icon: CommentIcon },
  qwoted100: { label: "Qwoted 100", color: "#3a27a5", background: "#edeafa", iconColor: "#533dd1", Icon: StarIcon },
  sample_available: { label: "Sample available", color: "#1f8a4d", background: "#e8f6ec", Icon: CheckIcon },
};

const SIZES = {
  md: { fontSize: 11, padding: "3px 9px", icon: 11, gap: 4 },
  sm: { fontSize: 10, padding: "2px 8px", icon: 9, gap: 4 },
};

export function SourceBadge({ type, size = "md" }) {
  const variant = VARIANTS[type];
  if (!variant) return null;
  const dims = SIZES[size] || SIZES.md;
  const { Icon } = variant;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: dims.gap,
        background: variant.background,
        color: variant.color,
        fontSize: dims.fontSize,
        fontWeight: 700,
        padding: dims.padding,
        borderRadius: 999,
        letterSpacing: "0.22px",
        lineHeight: 1.3,
        whiteSpace: "nowrap",
      }}
    >
      <Icon width={dims.icon} height={dims.icon} style={{ color: variant.iconColor || variant.color, flexShrink: 0 }} />
      {variant.label}
    </span>
  );
}

SourceBadge.propTypes = {
  type: PropTypes.oneOf(Object.keys(VARIANTS)).isRequired,
  size: PropTypes.oneOf(["md", "sm"]),
};

// Renders the badges whose keys are truthy in `types`, in the given order.
// Pass falsy entries freely: `[hit.qwoted100 && "qwoted100", ...]`.
export function SourceBadgeList({ types, size = "md", marginTop = 6 }) {
  const shown = (types || []).filter((type) => VARIANTS[type]);
  if (shown.length === 0) return null;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop }}>
      {shown.map((type) => (
        <SourceBadge key={type} type={type} size={size} />
      ))}
    </div>
  );
}

SourceBadgeList.propTypes = {
  // Callers pass falsy entries freely (e.g. `[hit.qwoted100 && "qwoted100", …]`); non-variant
  // values are filtered out above, so booleans are expected and valid here.
  types: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.bool])),
  size: PropTypes.oneOf(["md", "sm"]),
  marginTop: PropTypes.number,
};

export const BADGE_TYPES = Object.keys(VARIANTS);

export default SourceBadge;
